import React, { useState, useCallback, useRef } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Text, Divider, Link, Flex
} from 'components/ui';
import ShipAddressListItem from 'components/FrictionlessCheckout/DeliverToReleaseThree/ShipAddress/Section/ShipAddressListItem/ShipAddressListItem';
import { savedAddressesModalSelector } from 'components/RichProfile/MyAccount/AutoReplenishment/ManageAddress/SavedAddressesModal/savedAddressesModalSelector';
import { useSelector } from 'react-redux';
import { modal, colors } from 'style/config';

const DEFAULT_ADDRESS_COUNT = 3;

function AddressList({
    addresses = [],
    selectedAddressId = '',
    onAddressSelect = null,
    onAddressAdd = null,
    onAddressEdit = null,
    onAddressRemove = null
}) {
    const [addressesToDisplay, setAddressesToDisplay] = useState(DEFAULT_ADDRESS_COUNT);
    const addressRefs = useRef({});
    const {
        showLess, showMore, noAddresses, editLabel, removeLabel, editAddressLabel, removeAddressLabel, addShippingAddress
    } =
        useSelector(savedAddressesModalSelector);

    const handleShowMore = useCallback(() => {
        setAddressesToDisplay(addresses.length);

        // Scroll to newly revealed address
        setTimeout(() => {
            const lastVisibleIndex = addresses.length - 1;
            const refKey = `address_${lastVisibleIndex}`;

            addressRefs.current[refKey]?.scrollIntoView({
                block: 'start',
                behavior: 'smooth'
            });
        }, 100);
    }, [addresses.length]);

    /**
     * Handle Show Less
     */
    const handleShowLess = useCallback(() => {
        setAddressesToDisplay(DEFAULT_ADDRESS_COUNT);

        // Scroll to top
        setTimeout(() => {
            const refKey = 'address_0';

            addressRefs.current[refKey]?.scrollIntoView({
                block: 'start',
                behavior: 'smooth'
            });
        }, 100);
    }, []);

    /**
     * Returns addressId of the next address in the list after the default one.
     * We need it in case user wants to delete default address so we'll need to automatically set the new default address
     * @type {function(): *}
     */
    const getNextAddressIdAfterDefault = useCallback(() => addresses.filter(addr => !addr.isDefault)[0]?.addressId, [addresses]);

    if (addresses.length === 0) {
        return (
            <Box
                padding={5}
                textAlign='center'
            >
                <Text color='gray'>{noAddresses}</Text>
            </Box>
        );
    }

    const displayedAddresses = addresses.slice(0, addressesToDisplay);
    const showMoreLessButton = addresses.length > DEFAULT_ADDRESS_COUNT;
    const isShowingAll = addressesToDisplay >= addresses.length;

    return (
        <Box>
            {displayedAddresses.map((address, index) => (
                <div
                    key={address.addressId}
                    ref={el => (addressRefs.current[`address_${index}`] = el)}
                    data-at={Sephora.debug.dataAt('address_item')}
                >
                    <ShipAddressListItem
                        address={address}
                        currentShipAddressId={selectedAddressId}
                        onSelect={() => onAddressSelect?.(address.addressId)}
                        onEdit={() => onAddressEdit?.(address)}
                        onRemove={() => onAddressRemove?.(address.addressId, address.isDefault, address.isDefault && getNextAddressIdAfterDefault())}
                        isRadio={true}
                        labelName='addressSelection'
                        showTaxExemptAddress={false}
                        editLabel={editLabel}
                        removeLabel={removeLabel}
                        editAddressLabel={editAddressLabel}
                        removeAddressLabel={removeAddressLabel}
                        shouldDisplayButtons
                    />

                    {index < displayedAddresses.length - 1 && (
                        <Divider
                            marginY={4}
                            marginX={modal.outdentX}
                        />
                    )}
                </div>
            ))}

            <Divider
                marginY={4}
                marginX={modal.outdentX}
            />

            {showMoreLessButton ? (
                <Flex color={colors.gray}>
                    <Link
                        color='blue'
                        paddingY={2}
                        marginY={-2}
                        onClick={isShowingAll ? handleShowLess : handleShowMore}
                        data-at={Sephora.debug.dataAt('address_show_more_less')}
                    >
                        {isShowingAll ? showLess : showMore}
                    </Link>{' '}
                    <Box
                        borderLeft={1}
                        marginX={3}
                        borderColor='divider'
                        display='inline-block'
                    />
                    <Link
                        color='blue'
                        paddingY={2}
                        marginY={-2}
                        data-at={Sephora.debug.dataAt('addAddress')}
                        onClick={onAddressAdd}
                    >
                        {addShippingAddress}
                    </Link>
                </Flex>
            ) : (
                <Link
                    color='blue'
                    paddingY={2}
                    paddingLeft={1}
                    marginY={-2}
                    data-at={Sephora.debug.dataAt('addAddress')}
                    onClick={onAddressAdd}
                >
                    {addShippingAddress}
                </Link>
            )}
        </Box>
    );
}

export default wrapFunctionalComponent(AddressList, 'AddressList');
